/**
 * useDiscussContext — assemble the full `ExecutionDiscussContext` the
 * Discuss modal hands to the debug agent.
 *
 * Responsibilities:
 *
 * 1. Load the `Execution` via `useExecutionDetail` (which also live-polls
 *    while the execution is non-terminal).
 * 2. Walk `execution.steps`, harvest every agent `session_id` an
 *    `invoke_agent` step emitted (via `extractStepSessionId`), and the
 *    optional `user_id` partition-key hint (via
 *    `extractStepSessionUserId`).
 * 3. Dispatch `loadTranscriptChunk(sessionId, TURN_CAP, userIdHint)` for
 *    each harvested session id, then subscribe to the relevant
 *    `transcriptCache` slices to know when each one reaches a terminal
 *    state (`'complete'`, `'loaded'`, or `'error'`).
 * 4. In parallel, fire `generateExecutionSummary(executionId)` once.
 *    Non-terminal executions return 400 — caught and surfaced as a
 *    `summary: undefined` rather than blocking the whole context.
 * 5. Derive a unified `status` for the caller:
 *    - `'loading'`: execution not yet loaded, OR some sessions still in
 *      `'idle'`/`'loading'` AND nothing usable yet.
 *    - `'partial'`: execution loaded, at least one transcript reached a
 *      terminal state, others still loading.
 *    - `'ready'`: execution loaded AND every harvested session reached
 *      a terminal state (complete/loaded/error).
 *    - `'error'`: execution itself failed to load.
 *
 * The TURN_CAP=200 constant matches `PREFETCH_LIMIT` in
 * `ApprovalHeaderActions.tsx` and `TURN_CAP` in `AgentSessionModalBody.tsx`
 * so all consumers of the transcript cache request the same batch size
 * and avoid redundant fetches. Sessions with more turns than the cap
 * land in the cache with `status: 'loaded'` (not `'complete'`), which
 * we treat as ready-enough — the truncation note in the markdown blob
 * tells the debug agent that more events exist.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { useExecutionDetail } from '@/features/automations/execution/useExecutionDetail'
import {
  extractStepSessionId,
  extractStepSessionUserId,
} from '@/features/automations/types'
import type { ExecutionStep } from '@/features/automations/types'
import { useSessionsStore } from '@/features/sessions/store'
import type { TranscriptCache } from '@/features/sessions/types'
import { generateExecutionSummary } from '@/features/queue/api'
import { getDiscussAgentId } from '@/features/agents/discussAgent'
import { buildDiscussSystemMessage } from '@/features/session/lib/buildDiscussSystemMessage'
import type {
  ExecutionDiscussContext,
  DiscussSessionTranscript,
} from '@/features/session/types'

/**
 * Max number of turns requested per agent session. Mirrors the cap used
 * by `AgentSessionModalBody` and `ApprovalHeaderActions` so the cache
 * entries we populate are reusable across surfaces.
 */
export const DISCUSS_TURN_CAP = 200

/** Statuses we treat as "no longer making progress". */
const TERMINAL_TRANSCRIPT_STATUSES: ReadonlyArray<TranscriptCache['status']> = [
  'complete',
  'loaded',
  'error',
]

export type DiscussContextStatus = 'loading' | 'partial' | 'ready' | 'error'

export interface UseDiscussContextResult {
  context: ExecutionDiscussContext | null
  status: DiscussContextStatus
  /** Per-session error map keyed by `sessionId`. */
  perSessionErrors: Map<string, string>
  /** Top-level execution-fetch error, if any. */
  executionError: string | null
  /** Memoised markdown blob, present when `status === 'ready' | 'partial'`. */
  contextText: string | null
}

/**
 * Harvest the unique set of `(sessionId, userIdHint, stepIndex, stepName)`
 * tuples from an execution's steps.
 *
 * Dedup: if two steps somehow reference the same session id we keep the
 * first occurrence — easier to keep the markdown ordered by step.
 */
function harvestSessionRefs(
  steps: ExecutionStep[] | undefined,
): Array<{
  sessionId: string
  userIdHint: string | null
  stepIndex: number
  stepName: string
}> {
  const seen = new Set<string>()
  const out: Array<{
    sessionId: string
    userIdHint: string | null
    stepIndex: number
    stepName: string
  }> = []
  for (const step of steps ?? []) {
    const sid = extractStepSessionId(step.output)
    if (!sid || seen.has(sid)) continue
    seen.add(sid)
    out.push({
      sessionId: sid,
      userIdHint: extractStepSessionUserId(step.output),
      stepIndex: step.index,
      stepName: step.name,
    })
  }
  return out
}

export function useDiscussContext(
  executionId: string | null,
  options: { attentionItemId?: string | null } = {},
): UseDiscussContextResult {
  const attentionItemId = options.attentionItemId ?? null
  const {
    execution,
    error: executionError,
  } = useExecutionDetail(executionId ?? undefined)

  // ── Summary fetch (one-shot, non-blocking) ─────────────────────────────
  const [summary, setSummary] = useState<Record<string, unknown> | undefined>(
    undefined,
  )
  const lastSummaryExecutionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!executionId) {
      setSummary(undefined)
      lastSummaryExecutionRef.current = null
      return
    }
    if (lastSummaryExecutionRef.current === executionId) return
    lastSummaryExecutionRef.current = executionId

    let cancelled = false
    generateExecutionSummary(executionId)
      .then((resp) => {
        if (cancelled) return
        if (resp.execution_summary && typeof resp.execution_summary === 'object') {
          setSummary(resp.execution_summary as Record<string, unknown>)
        }
      })
      .catch(() => {
        // Non-terminal executions return 400; transient errors are
        // also acceptable — the summary is a nice-to-have, not a
        // blocker for the rest of the context.
        if (!cancelled) setSummary(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [executionId])

  // ── Harvest session refs from execution steps ──────────────────────────
  const sessionRefs = useMemo(
    () => (execution ? harvestSessionRefs(execution.steps) : []),
    [execution],
  )

  // ── Dispatch transcript loads ─────────────────────────────────────────
  const loadTranscriptChunk = useSessionsStore((s) => s.loadTranscriptChunk)
  // Track which sessionIds we've already dispatched so React-strict-mode
  // double-invokes don't fire two requests per session (the store has its
  // own loading-short-circuit but we avoid even calling it twice).
  const dispatchedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (sessionRefs.length === 0) return
    for (const ref of sessionRefs) {
      if (dispatchedRef.current.has(ref.sessionId)) continue
      dispatchedRef.current.add(ref.sessionId)
      void loadTranscriptChunk(
        ref.sessionId,
        DISCUSS_TURN_CAP,
        ref.userIdHint ?? null,
      )
    }
  }, [sessionRefs, loadTranscriptChunk])

  // Reset the dispatched-set when the execution changes — without this,
  // navigating between two executions in the same modal session would
  // skip dispatch for shared session ids and never repopulate the cache
  // if it had been invalidated.
  useEffect(() => {
    dispatchedRef.current = new Set()
  }, [executionId])

  // ── Subscribe to the relevant cache slices ────────────────────────────
  //
  // We deliberately read the *entire* transcriptCache via a stable
  // selector and slice it inside the component. This avoids the zustand
  // gotcha where returning a new Array from the selector on every render
  // causes infinite re-renders — the Map identity from the store is
  // stable across unrelated updates because the reducers mutate it via
  // `new Map(...)` only when a session entry changes.
  const transcriptCache = useSessionsStore((s) => s.transcriptCache)
  const sessionsList = useSessionsStore((s) => s.sessions)

  // ── Assemble per-session transcripts ──────────────────────────────────
  const sessionTranscripts: DiscussSessionTranscript[] = useMemo(() => {
    return sessionRefs.map((ref): DiscussSessionTranscript => {
      const cached = transcriptCache.get(ref.sessionId)
      const meta = sessionsList.find((s) => s.sessionId === ref.sessionId)
      const status = cached?.status

      if (status === 'error') {
        return {
          sessionId: ref.sessionId,
          agentId: meta?.agentId ?? null,
          agentName: meta?.agentName ?? null,
          stepIndex: ref.stepIndex,
          stepName: ref.stepName,
          turns: [],
          truncated: false,
          error: cached?.error ?? 'Failed to load transcript',
        }
      }

      const turns = cached?.turns ?? []
      return {
        sessionId: ref.sessionId,
        agentId: meta?.agentId ?? null,
        agentName: meta?.agentName ?? null,
        stepIndex: ref.stepIndex,
        stepName: ref.stepName,
        turns,
        // `loaded` (not `complete`) means the backend reported more turns
        // available after our requested page. Surface that as truncated
        // in the markdown so the debug agent can ask for narrower scope.
        truncated: status === 'loaded',
      }
    })
  }, [sessionRefs, transcriptCache, sessionsList])

  // ── Derive overall status ─────────────────────────────────────────────
  const perSessionErrors = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of sessionTranscripts) {
      if (t.error) m.set(t.sessionId, t.error)
    }
    return m
  }, [sessionTranscripts])

  const status: DiscussContextStatus = useMemo(() => {
    if (executionError) return 'error'
    if (!execution) return 'loading'
    if (sessionRefs.length === 0) return 'ready' // execution with no agent sessions

    const terminalCount = sessionRefs.reduce((n, ref) => {
      const s = transcriptCache.get(ref.sessionId)?.status
      return s && (TERMINAL_TRANSCRIPT_STATUSES as ReadonlyArray<string>).includes(s)
        ? n + 1
        : n
    }, 0)

    if (terminalCount === sessionRefs.length) return 'ready'
    if (terminalCount > 0) return 'partial'
    return 'loading'
  }, [execution, executionError, sessionRefs, transcriptCache])

  // ── Assemble the final context object ─────────────────────────────────
  const context: ExecutionDiscussContext | null = useMemo(() => {
    if (!execution || !executionId) return null
    if (status === 'error') return null
    // Build whenever there's something usable. `partial` is fine — the
    // markdown serializer marks per-session error/loading state inline.
    return {
      kind: 'execution_discuss',
      debugAgentId: getDiscussAgentId(),
      executionId,
      automationId: execution.automationId ?? null,
      // 3700a Objective 17 → 3701h: thread the AttentionItem id from
      // the caller instead of hardcoding null. The markdown serializer
      // includes the id in the execution_context envelope so the
      // debugger agent can reference the approval back to the operator.
      attentionItemId,
      executionSnapshot: execution,
      sessionTranscripts,
      summary,
    }
  }, [execution, executionId, attentionItemId, sessionTranscripts, summary, status])

  // Build the markdown blob whenever there's something usable. Only
  // skipped while the execution itself is still loading or in an error
  // state — partial transcripts are acceptable; the builder marks each
  // session's load state inline so the agent knows what's missing.
  const contextText: string | null = useMemo(() => {
    if (!context) return null
    if (status === 'loading' || status === 'error') return null
    return buildDiscussSystemMessage(context)
  }, [context, status])

  return {
    context,
    status,
    perSessionErrors,
    executionError,
    contextText,
  }
}
